import {
  AiBuilderText,
  FlowOperationType,
  isNil,
  RETRYABLE_SYSTEM_MESSAGES,
} from '@activepieces/shared';
import { t } from 'i18next';
import { Undo2, RefreshCw } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useBuilderStateContext } from '../builder-hooks';

import {
  AiBuilderChatMessage,
  hasFlowChanged,
  interpolateMissingIcuParams,
  pieceNamespaceFor,
  translateFieldNameList,
} from './ai-builder-utils';
import { AnimatedKhubLogo } from './animated-khub-logo';
import { TurnChangesSummary } from './turn-changes-summary';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';

interface ChatMessageProps {
  message: AiBuilderChatMessage;
  isLastMessage: boolean;
  onUndo?: () => void;
  onRetry?: () => void;
  showAvatar?: boolean;
  muted?: boolean;
  thinking?: boolean;
  children?: ReactNode;
}

interface ChatBoxProps {
  children: string;
  variant?: 'user' | 'bot';
  muted?: boolean;
}

const ChatBox = ({
  children,
  variant = 'bot',
  muted = false,
}: ChatBoxProps) => (
  <div
    className={`break-words overflow-hidden ${
      variant === 'user'
        ? 'max-w-xs lg:max-w-md bg-gray-100 dark:bg-gray-800 dark:text-gray-100 rounded-2xl px-5 py-2'
        : 'min-w-0 pr-1'
    }`}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      className={`prose prose-sm dark:prose-invert max-w-none [&>*+*]:mt-3 [&_ul]:mt-1 [&_ol]:mt-1 ${
        muted ? 'text-muted-foreground' : ''
      }`}
    >
      {children}
    </ReactMarkdown>
  </div>
);

const translateWithPieceNs = (
  content: string,
  params: Record<string, string>,
): string => {
  const { pieceName, ...interpolationParams } = params;
  const ns = pieceNamespaceFor(pieceName);

  const translatedParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(interpolationParams)) {
    if (!ns) {
      translatedParams[key] = value;
      continue;
    }
    // A bolded list is several keys at once — each name is looked up alone
    translatedParams[key] = value.includes('**')
      ? translateFieldNameList(value, (name) => t(name, { ns }))
      : t(value, { ns });
  }

  const translated = t(content, translatedParams);
  return interpolateMissingIcuParams(translated, translatedParams);
};

const displayedText = (text: AiBuilderText): string =>
  text.kind === 'key' ? translateWithPieceNs(text.key, text.params) : text.text;

const BotAvatar = ({ thinking = false }: { thinking?: boolean }) => (
  <div className="shrink-0 pr-2">
    <AnimatedKhubLogo animate={thinking} className="h-6 w-6" />
  </div>
);

interface ActionButtonsProps {
  canUndo: boolean;
  isLastMessage: boolean;
  onUndo: () => void;
  onRetry?: () => void;
}

const ActionButtons = ({
  canUndo,
  isLastMessage,
  onUndo,
  onRetry,
}: ActionButtonsProps) => {
  if (!canUndo && !isLastMessage) return null;

  return (
    <div className="flex gap-1 mt-1">
      {canUndo && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={onUndo}
            >
              <Undo2 className="w-3.5 h-3.5 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('Undo')}</TooltipContent>
        </Tooltip>
      )}
      {isLastMessage && onRetry && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={onRetry}
            >
              <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('Retry')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

/**
 * Retry belongs to FAILED turns only. A normal turn acts on the flow (it
 * builds steps, runs tests), so replaying it is not "regenerate an answer" —
 * free text, the wizard buttons and undo cover that. It replays the turn's own
 * prompt, never the last user line lying around in the chat.
 */
const canRetry = (message: AiBuilderChatMessage): boolean =>
  !isNil(message.sourceInput) &&
  message.body.kind === 'key' &&
  RETRYABLE_SYSTEM_MESSAGES.includes(message.body.key);

const canUndoChanges = (message: AiBuilderChatMessage) => {
  if (!message.previousFlowVersion || !message.newFlowVersion) {
    return false;
  }
  return hasFlowChanged(message.previousFlowVersion, message.newFlowVersion);
};

export const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(
  (
    {
      message,
      isLastMessage,
      onUndo,
      onRetry,
      showAvatar = true,
      muted = false,
      thinking = false,
      children,
    },
    ref,
  ) => {
    const { applyOperation, exitStepSettings } = useBuilderStateContext(
      (state) => ({
        applyOperation: state.applyOperation,
        exitStepSettings: state.exitStepSettings,
      }),
    );

    const canUndo = isLastMessage && canUndoChanges(message);

    const handleRevert = () => {
      if (!message.previousFlowVersion) return;
      exitStepSettings();

      applyOperation(
        {
          type: FlowOperationType.REPLACE_VERSION,
          request: { flowVersion: message.previousFlowVersion },
        },
        () => {
          toast({
            variant: 'destructive',
            title: t('Error'),
            description: t('Cannot revert to previous flow'),
          });
        },
      );
      onUndo?.();
    };

    const isUser = message.userType === 'user';
    const isBot = message.userType === 'bot';

    return (
      <div
        ref={ref}
        className={`flex gap-2 mx-2 ${
          isUser ? 'justify-end' : 'justify-start'
        }`}
      >
        {isBot && (
          <>
            {showAvatar ? (
              <BotAvatar thinking={thinking} />
            ) : (
              <div className="shrink-0 w-6 mr-2" />
            )}
            <div className="w-full min-w-0">
              <ChatBox variant="bot" muted={muted}>
                {[message.prefix, displayedText(message.body)]
                  .filter(Boolean)
                  .join('\n\n')}
              </ChatBox>
              {message.changes && (
                <TurnChangesSummary changes={message.changes} />
              )}
              {children}
              <ActionButtons
                canUndo={canUndo}
                onUndo={handleRevert}
                onRetry={canRetry(message) ? onRetry : undefined}
                isLastMessage={isLastMessage}
              />
            </div>
          </>
        )}
        {isUser && (
          <div className="pl-8">
            <ChatBox variant="user">
              {message.body.kind === 'text'
                ? message.body.text
                : message.body.key}
            </ChatBox>
          </div>
        )}
      </div>
    );
  },
);

ChatMessage.displayName = 'ChatMessage';
